import { useCallback, useEffect, useMemo, useState } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { createAimTheme } from './theme';
import { COLOR_MODE_STORAGE_KEY, ColorModeContext, type ColorModePreference } from './colorMode';
import type { ColorMode } from './tokens';
import './global.css';

function readStored(): ColorModePreference {
  try {
    const v = window.localStorage.getItem(COLOR_MODE_STORAGE_KEY);
    return v === 'light' || v === 'dark' || v === 'system' ? v : 'system';
  } catch {
    return 'system';
  }
}

function systemMode(): ColorMode {
  return typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';
}

export interface AimThemeProviderProps {
  children: React.ReactNode;
  /** Força o modo (usado nos testes e no ThemePreview). */
  mode?: ColorModePreference;
}

/**
 * `ThemeProvider` + `CssBaseline` + tokens CSS do Zarya (T011).
 * O modo escolhido vira `data-theme` no `<html>`, que é o que os
 * `*.module.css` portados de `zarya-components.css` leem.
 */
export function AimThemeProvider({ children, mode: forced }: AimThemeProviderProps) {
  const [preference, setPreferenceState] = useState<ColorModePreference>(
    () => forced ?? readStored(),
  );
  const [systemPreferred, setSystemPreferred] = useState<ColorMode>(systemMode);

  useEffect(() => {
    if (forced) setPreferenceState(forced);
  }, [forced]);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => setSystemPreferred(mq.matches ? 'dark' : 'light');
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const mode: ColorMode = preference === 'system' ? systemPreferred : preference;

  const setPreference = useCallback((p: ColorModePreference) => {
    setPreferenceState(p);
    try {
      window.localStorage.setItem(COLOR_MODE_STORAGE_KEY, p);
    } catch {
      /* modo privado — a preferência só não persiste */
    }
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    if (preference === 'system') root.removeAttribute('data-theme');
    else root.setAttribute('data-theme', preference);
    root.style.colorScheme = mode;
  }, [preference, mode]);

  const theme = useMemo(() => createAimTheme(mode), [mode]);
  const ctx = useMemo(
    () => ({ preference, mode, setPreference }),
    [preference, mode, setPreference],
  );

  return (
    <ColorModeContext.Provider value={ctx}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}
